import {
  BulkDeleteWithConfirmButton,
  Datagrid,
  DeleteWithConfirmButton,
  Edit,
  FunctionField,
  List,
  Pagination,
  SaveButton,
  SelectArrayInput,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  useListContext,
  useNotify,
  useRedirect,
  Validator,
} from "react-admin";
import "./styles.css";
import { Params, Link as RouterLink, useParams } from "react-router-dom";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField as MuiTextField,
} from "@mui/material";
import { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import { useCustomPermissions } from '../../hooks/usePermissions';
import { PermissionGuard } from '../../components/PermissionGuard';

const VariablePagination = (props: any) => (
  <Pagination rowsPerPageOptions={[25, 50, 100]} {...props} />
);

const SearchBar = ({
  defaultKeyword,
  onSearch,
}: {
  defaultKeyword: string;
  onSearch: (kw: string) => void;
}) => {
  const [keyword, setKeyword] = useState(defaultKeyword);

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <MuiTextField
        size="small"
        placeholder="Search keyword"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
      />
      <Button variant="contained" onClick={() => onSearch(keyword)}>
        Search
      </Button>
    </Box>
  );
};

const ActionsFunctionField = ({ agentId }: { agentId?: string }) => {
  const { hasPermission } = useCustomPermissions();

  return (
    <FunctionField
      label="Actions"
      render={(record: any) => {
        console.log('Record data:', record);

        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <PermissionGuard resource="unanswered_question" action="edit">
              <Button
                component={RouterLink}
                to={`/unanswered-question/${agentId}/${record.id}`}
                variant="outlined"
                size="small"
              >
                <EditIcon style={{ fontSize: 16, marginRight: 8 }} /> Edit
              </Button>
            </PermissionGuard>

            {record.chat_id?.split('_')[1] && (
              <Button
                component="a"
                href={`https://my.livechatinc.com/archives/${record.chat_id.split('_')[1]}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                color="info"
              >
                Track
              </Button>
            )}

            <PermissionGuard resource="unanswered_question" action="delete">
              <DeleteWithConfirmButton
                record={record}
                confirmTitle="Are you sure?"
                confirmContent="You won't be able to revert this!"
                mutationMode="pessimistic"
                redirect={`/unanswered-question/${agentId}`}
              />
            </PermissionGuard>
          </Box>
        );
      }}
    />
  );
};

const QuestionListApi = ({ agentId }: { agentId?: string }) => {
  const { filterValues = {}, setFilters } = useListContext();
  const { hasPermission } = useCustomPermissions();

  const handleSearch = (kw: string) => {
    if (typeof setFilters === "function") {
      setFilters({ ...filterValues, keyword: kw }, null);
    }
  };
  return (
    <Card sx={{ m: 2 }}>
      <CardHeader title="Unanswered Questions" />
      <CardContent>
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
          <SearchBar
            defaultKeyword={filterValues.keyword || ""}
            onSearch={handleSearch}
          />
        </Box>
        <Datagrid
          bulkActionButtons={
            hasPermission('unanswered_question', 'delete') ? (
              <BulkDeleteWithConfirmButton
                confirmTitle="Are you sure?"
                confirmContent="This will permanently delete the selected questions."
                mutationMode="pessimistic"
              />
            ) : false
          }
        >
          <TextField
            source="question"
            label="Question"
            cellClassName="column-question"
          />
          {(hasPermission('unanswered_question', 'edit') || hasPermission('unanswered_question', 'delete')) && (
            <FunctionField
              label="Actions"
              render={(record: any) => <ActionsFunctionField agentId={agentId} />}
              cellClassName="column-actions"
            />
          )}
        </Datagrid>
      </CardContent>
    </Card>
  );
};

export const QuestionList = (props: any) => {
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  return (
    <List
      {...props}
      component="div"
      perPage={25}
      pagination={<VariablePagination />}
    >
      <QuestionListApi agentId={agentId} />
    </List>
  );
};

//edit
const tagOptions = [
  { id: "defaultTag", name: "Default Tag" },
  { id: "tag", name: "Tag" },
  { id: "deposit", name: "Deposit" },
  { id: "dynamic", name: "Dynamic" },
  { id: "context", name: "Context" },
];

const requiredTrimmed =
  (message = "Required"): Validator =>
  (value: string | null | undefined) =>
    value?.trim() ? undefined : message;

const transformEdit = (data: any) => ({
  ...data,
  key: data.key?.trim(),
  value: data.value?.trim(),
});

const CustomToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);
export const QuestionEdit = (props: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  return (
    <Edit
      {...props}
      title={false}
      component="div"
      transform={(data: any) => ({ ...transformEdit(data), agent_id: agentId })}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Updated successfully", { type: "success" });
          redirect(`/unanswered-question/${agentId || ""}`);
        },
        onError: (error: any) =>
          notify(`Error: ${error?.message}`, { type: "error" }),
      }}
    >
      <Card sx={{ margin: 2 }}>
        <CardHeader title="Dynamic Context Update" />
        <CardContent>
          <SimpleForm toolbar={<CustomToolbar />}>
            <TextInput
              source="question"
              label="Question"
              fullWidth
              validate={requiredTrimmed("Question is required")}
            />
            <TextInput
              source="answer"
              label="Answer"
              fullWidth
              validate={requiredTrimmed("Answer is required")}
            />

            <SelectArrayInput
              source="tags"
              label="Tags"
              choices={tagOptions}
              fullWidth
            />
          </SimpleForm>
        </CardContent>
      </Card>
    </Edit>
  );
};
